/**
 * The console's HTTP calls for Fleet health and Trends (docs/CONTRACT-fleet-governance.md section 2.1), over `fetch` with
 * lib/api.ts's exported `requestInit` and `ApiError`, so lib/api.ts itself is not edited (the PETS PR edits it). The
 * answers and refusals read exactly as lib/api.ts's own `http` reads them: 401 and 403 as `unauthenticated` and
 * `forbidden`, any other refusal as the service's own `message` or `error` with its status and body.
 */
import { ApiError, requestInit } from './api';

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, requestInit(init));
  if (res.status === 401) throw new ApiError('unauthenticated', 401);
  if (res.status === 403) throw new ApiError('forbidden', 403);
  if (!res.ok) {
    const body = (await res.json().catch(() => undefined)) as Record<string, unknown> | undefined;
    const message = typeof body?.message === 'string' ? body.message : typeof body?.error === 'string' ? body.error : `request failed: ${res.status}`;
    throw new ApiError(message, res.status, body);
  }
  return (await res.json()) as T;
}

/** A GET under /v1/admin (the session cookie rides along). */
export function adminGet<T>(path: string): Promise<T> {
  return call<T>(path);
}

/** A PUT or POST under /v1/admin with its JSON body (`body: JSON.stringify(...)`; a JSON content-type is added). */
export function adminSend<T>(path: string, init: RequestInit): Promise<T> {
  return call<T>(path, init);
}

/**
 * The query string for a list or a section: every set value, in the order given; nothing when every value is empty. A
 * `true` is `1` (`showGone=1`); a `false`, an empty string, null and undefined are left out.
 */
export function queryOf(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '' || value === false) continue;
    search.set(key, value === true ? '1' : String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

/** The status a failed call answered with, or 0 when it never reached the service. */
export function statusOf(error: unknown): number {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === 'number' ? status : 0;
}
